import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Briefcase, Cpu, FileText, Heart, Laptop, ListChecks, Phone, Pill, Shirt, Sun, Bus,
  Printer, MessageSquare, Plus, Pencil, Trash2, RotateCcw, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  FileText, Laptop, Shirt, Sun, Heart, Pill, Bus, Briefcase, Phone, Cpu, ListChecks,
};

const PRIORITY_STYLE = {
  mandatory: { label: 'Must', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300', dot: 'bg-rose-500' },
  recommended: { label: 'Rec', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300', dot: 'bg-sky-500' },
  do_not_carry: { label: 'Don\'t', cls: 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200 line-through decoration-rose-500', dot: 'bg-zinc-400' },
};

const STORAGE_KEY_PREFIX = 'wisewit_travel_checklist_checked_v1';

export const TravelChecklist = ({ isAdmin = false }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [travelType, setTravelType] = useState('domestic');
  const [showOnlyMandatory, setShowOnlyMandatory] = useState(false);
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  // Admin CRUD
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    section: '', section_icon: 'ListChecks', section_order: 11,
    subcategory: '', name: '', priority: 'mandatory', travel_type: 'both',
  });
  // WhatsApp dialog
  const [isWaOpen, setIsWaOpen] = useState(false);
  const [waPhone, setWaPhone] = useState('');

  const storageKey = `${STORAGE_KEY_PREFIX}_${travelType}`;

  const fetchChecklist = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/travel/checklist`, {
        params: { travel_type: travelType },
        withCredentials: true,
      });
      setSections(res.data.sections || []);
      // expand all by default
      const exp = {};
      (res.data.sections || []).forEach((s) => { exp[s.section] = true; });
      setExpandedSections(exp);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load checklist');
    } finally {
      setLoading(false);
    }
  }, [travelType]);

  useEffect(() => { fetchChecklist(); }, [fetchChecklist]);

  // Load/save checked state from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      setChecked(raw ? JSON.parse(raw) : {});
    } catch { setChecked({}); }
  }, [storageKey]);

  const toggleItem = (id) => {
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const resetChecks = () => {
    setChecked({});
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    toast.success('Checklist reset');
  };

  const filteredSections = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sections.map((s) => ({
      ...s,
      subcategories: s.subcategories
        .map((sub) => ({
          ...sub,
          items: sub.items.filter((i) => {
            if (showOnlyMandatory && i.priority !== 'mandatory') return false;
            if (term && !i.name.toLowerCase().includes(term)) return false;
            return true;
          }),
        }))
        .filter((sub) => sub.items.length > 0),
    })).filter((s) => s.subcategories.length > 0);
  }, [sections, search, showOnlyMandatory]);

  const stats = useMemo(() => {
    let total = 0, done = 0, mand = 0, mandDone = 0;
    filteredSections.forEach((s) => s.subcategories.forEach((sub) => sub.items.forEach((it) => {
      total++;
      if (checked[it.id]) done++;
      if (it.priority === 'mandatory') {
        mand++;
        if (checked[it.id]) mandDone++;
      }
    })));
    return { total, done, mand, mandDone };
  }, [filteredSections, checked]);

  const handlePrint = () => {
    window.print();
  };

  const openWhatsApp = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/travel/checklist/whatsapp`, {
        travel_type: travelType,
        phone: waPhone.replace(/\D/g, ''),
      }, { withCredentials: true });
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
      setIsWaOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to build WhatsApp link');
    }
  };

  // Admin CRUD
  const openAddItem = () => {
    setEditingItem(null);
    setForm({
      section: sections[0]?.section || '',
      section_icon: sections[0]?.icon || 'ListChecks',
      section_order: sections[0]?.order || 11,
      subcategory: '', name: '', priority: 'recommended', travel_type: 'both',
    });
    setIsManageOpen(true);
  };

  const openEditItem = (item, parentSection) => {
    setEditingItem(item);
    setForm({
      section: item.section,
      section_icon: parentSection?.icon || 'ListChecks',
      section_order: parentSection?.order || 11,
      subcategory: item.subcategory,
      name: item.name,
      priority: item.priority,
      travel_type: item.travel_type,
    });
    setIsManageOpen(true);
  };

  const saveItem = async () => {
    if (!form.section || !form.subcategory || !form.name) {
      toast.error('Section, sub-category and name are required');
      return;
    }
    try {
      if (editingItem) {
        await axios.put(`${API_URL}/api/travel/checklist/item/${editingItem.id}`, form, { withCredentials: true });
        toast.success('Item updated');
      } else {
        await axios.post(`${API_URL}/api/travel/checklist/item`, form, { withCredentials: true });
        toast.success('Item added');
      }
      setIsManageOpen(false);
      fetchChecklist();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/travel/checklist/item/${item.id}`, { withCredentials: true });
      toast.success('Item deleted');
      fetchChecklist();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('This will replace the entire checklist with the default Wisewit list. Continue?')) return;
    try {
      await axios.post(`${API_URL}/api/travel/checklist/reset`, {}, { withCredentials: true });
      toast.success('Defaults restored');
      fetchChecklist();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset');
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <Card className="print:hidden">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 bg-muted rounded-full p-0.5">
            {['domestic', 'international'].map((t) => (
              <button
                key={t}
                onClick={() => setTravelType(t)}
                className={`px-4 py-1.5 text-xs font-medium rounded-full transition ${travelType === t ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`tc-tab-${t}`}
              >
                {t === 'domestic' ? 'Domestic' : 'International'}
              </button>
            ))}
          </div>
          <Input
            placeholder="Search items…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs h-9"
            data-testid="tc-search"
          />
          <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOnlyMandatory}
              onChange={(e) => setShowOnlyMandatory(e.target.checked)}
              data-testid="tc-only-mandatory"
            />
            Only Mandatory
          </label>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={resetChecks} className="gap-1.5" data-testid="tc-reset-checks">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1.5" data-testid="tc-print">
              <Printer className="w-3.5 h-3.5" /> Print
            </Button>
            <Button size="sm" onClick={() => setIsWaOpen(true)} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tc-whatsapp">
              <MessageSquare className="w-3.5 h-3.5" /> Share via WhatsApp
            </Button>
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={openAddItem} className="gap-1.5" data-testid="tc-admin-add">
                  <Plus className="w-3.5 h-3.5" /> Add Item
                </Button>
                <Button variant="outline" size="sm" onClick={resetToDefaults} className="gap-1.5" data-testid="tc-admin-reset">
                  <RotateCcw className="w-3.5 h-3.5" /> Restore Defaults
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Progress strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print:hidden">
        <div className="rounded-lg border p-3 bg-card">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Items packed</p>
          <p className="text-xl font-semibold mt-0.5"><span className="text-emerald-600 dark:text-emerald-400">{stats.done}</span> / {stats.total}</p>
        </div>
        <div className="rounded-lg border p-3 bg-card">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Mandatory done</p>
          <p className="text-xl font-semibold mt-0.5"><span className="text-rose-600 dark:text-rose-400">{stats.mandDone}</span> / {stats.mand}</p>
        </div>
        <div className="rounded-lg border p-3 bg-card col-span-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Completion</p>
          <div className="mt-1.5 h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all" style={{ width: `${stats.total ? (stats.done * 100) / stats.total : 0}%` }} />
          </div>
        </div>
      </div>

      {/* Print header (only visible when printing) */}
      <div className="hidden print:block">
        <h1 className="text-2xl font-bold">Wisewit Travel Checklist</h1>
        <p className="text-sm text-muted-foreground">Trip type: {travelType === 'domestic' ? 'Domestic' : 'International'} · Generated {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
        <hr className="my-3" />
      </div>

      {/* Checklist */}
      {loading ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Loading checklist…</CardContent></Card>
      ) : filteredSections.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No items match your filters.</CardContent></Card>
      ) : (
        <div className="space-y-3 travel-checklist-print">
          {filteredSections.map((section) => {
            const Icon = ICON_MAP[section.icon] || ListChecks;
            const isExpanded = expandedSections[section.section] !== false;
            return (
              <Card key={section.section} className="overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-4 hover:bg-muted/40 transition print:cursor-default"
                  onClick={() => setExpandedSections({ ...expandedSections, [section.section]: !isExpanded })}
                  data-testid={`tc-section-${section.order}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-base">{section.section}</p>
                      <p className="text-xs text-muted-foreground">{section.subcategories.reduce((n, sub) => n + sub.items.length, 0)} items · {section.subcategories.length} sub-categories</p>
                    </div>
                  </div>
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground print:hidden" /> : <ChevronRight className="w-4 h-4 text-muted-foreground print:hidden" />}
                </button>
                {(isExpanded || typeof window === 'undefined') && (
                  <div className="border-t bg-muted/20 print:bg-white">
                    {section.subcategories.map((sub) => (
                      <div key={sub.name} className="px-4 py-3 border-b last:border-b-0">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{sub.name}</p>
                        <ul className="space-y-1.5">
                          {sub.items.map((it) => {
                            const style = PRIORITY_STYLE[it.priority] || PRIORITY_STYLE.recommended;
                            const isChecked = !!checked[it.id];
                            return (
                              <li
                                key={it.id}
                                className={`flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-background transition group ${isChecked ? 'opacity-60' : ''}`}
                              >
                                <label className="flex items-center gap-3 flex-1 cursor-pointer min-w-0">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleItem(it.id)}
                                    className="w-4 h-4 shrink-0"
                                    data-testid={`tc-item-${it.id}`}
                                  />
                                  <span className={`text-sm flex-1 ${isChecked ? 'line-through text-muted-foreground' : ''}`}>{it.name}</span>
                                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 ${style.cls} border-0`}>{style.label}</Badge>
                                </label>
                                {isAdmin && (
                                  <span className="flex gap-1 opacity-0 group-hover:opacity-100 transition print:hidden">
                                    <button onClick={(e) => { e.stopPropagation(); openEditItem(it, section); }} className="p-1 rounded hover:bg-muted" data-testid={`tc-edit-${it.id}`}>
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); deleteItem(it); }} className="p-1 rounded hover:bg-rose-100 text-rose-600" data-testid={`tc-delete-${it.id}`}>
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Item Dialog */}
      <Dialog open={isManageOpen} onOpenChange={setIsManageOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add Checklist Item'}</DialogTitle>
            <DialogDescription>Items are visible to all employees on the Travel page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Section</Label>
                <Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} placeholder="e.g. Electronics" data-testid="tc-form-section" />
              </div>
              <div>
                <Label className="text-xs">Sub-category</Label>
                <Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} placeholder="e.g. Cabin Bag" data-testid="tc-form-sub" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Item Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Universal Travel Adapter" data-testid="tc-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mandatory">Mandatory</SelectItem>
                    <SelectItem value="recommended">Recommended</SelectItem>
                    <SelectItem value="do_not_carry">Do Not Carry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Travel Type</Label>
                <Select value={form.travel_type} onValueChange={(v) => setForm({ ...form, travel_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="domestic">Domestic only</SelectItem>
                    <SelectItem value="international">International only</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsManageOpen(false)}>Cancel</Button>
              <Button onClick={saveItem} data-testid="tc-form-save">{editingItem ? 'Update' : 'Add'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Dialog */}
      <Dialog open={isWaOpen} onOpenChange={setIsWaOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700"><MessageSquare className="w-5 h-5" /> Share via WhatsApp</DialogTitle>
            <DialogDescription>
              Pre-fills WhatsApp with the {travelType} checklist. Optionally add a recipient phone number (with country code) to send directly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Recipient phone (optional, with country code)</Label>
              <Input
                placeholder="91XXXXXXXXXX"
                value={waPhone}
                onChange={(e) => setWaPhone(e.target.value)}
                data-testid="tc-wa-phone"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Leave empty to open WhatsApp's contact picker.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsWaOpen(false)}>Cancel</Button>
              <Button onClick={openWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tc-wa-go">Open WhatsApp</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TravelChecklist;
